/**
 * AutomationPanel — Per-department automation control
 * Shows in ScraperDashboard under a new "Automation" tab
 * Lets admin enable/configure auto-scrape and auto-classify per dept
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ZapOff, Play, Pause, Clock, CheckCircle, AlertCircle,
  Settings, ChevronDown, ChevronRight, RefreshCw, Activity
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { confirmDialog } from '../ConfirmDialog';
import { toast } from '../Toast';
import useDepartments from '../../hooks/useDepartments';
import './AutomationPanel.css';

const TYPE_META = {
  scrape_gaps: {
    label: 'Auto-Scrape Gaps',
    icon: '🔍',
    desc: 'Automatically finds and scrapes unfilled months',
    color: '#3b82f6',
  },
  classify_low: {
    label: 'Auto-Classify Low',
    icon: '🧠',
    desc: 'Re-classifies low/very-low confidence items',
    color: '#8b5cf6',
  },
  reclassify_partial: {
    label: 'Auto-Reclassify Partial',
    icon: '♻️',
    desc: 'Reclassifies medium-confidence items older than 14 days',
    color: '#10b981',
  },
};

const StatusDot = ({ running }) => (
  <span className={`ap-status-dot ${running ? 'ap-status-dot--running' : 'ap-status-dot--stopped'}`} />
);

const DeptRow = ({ dept, configs, onToggle, runs }) => {
  const [expanded, setExpanded] = useState(false);
  const deptConfigs = configs.filter(c => c.dept_code === dept.code);
  const deptRuns = runs.filter(r => r.dept_code === dept.code).slice(0, 3);
  const anyEnabled = deptConfigs.some(c => c.enabled);

  return (
    <div className={`ap-dept-row ${anyEnabled ? 'ap-dept-row--active' : ''}`}>
      <div className="ap-dept-header" onClick={() => setExpanded(e => !e)}>
        <div className="ap-dept-info">
          <span className={`ap-dept-indicator ${anyEnabled ? 'ap-dept-indicator--on' : ''}`} />
          <span className="ap-dept-code">{dept.code}</span>
          {dept.bu && <span className="ap-dept-bu">({dept.bu})</span>}
          <span className="ap-dept-name">{dept.name}</span>
        </div>
        <div className="ap-dept-meta">
          {deptConfigs.filter(c => c.enabled).length > 0 && (
            <span className="ap-dept-active-count">
              {deptConfigs.filter(c => c.enabled).length} active
            </span>
          )}
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            className="ap-dept-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <div className="ap-type-grid">
              {Object.entries(TYPE_META).map(([type, meta]) => {
                const cfg = deptConfigs.find(c => c.automation_type === type);
                const enabled = cfg?.enabled ?? false;
                return (
                  <div
                    key={type}
                    className={`ap-type-card ${enabled ? 'ap-type-card--enabled' : ''}`}
                    style={{ '--type-color': meta.color }}
                  >
                    <div className="ap-type-header">
                      <span className="ap-type-icon">{meta.icon}</span>
                      <span className="ap-type-label">{meta.label}</span>
                      <button
                        className={`ap-toggle ${enabled ? 'ap-toggle--on' : 'ap-toggle--off'}`}
                        onClick={() => onToggle(dept.code, dept.name, type, !enabled, cfg?.id)}
                        title={enabled ? 'Disable' : 'Enable'}
                      >
                        <motion.span
                          className="ap-toggle-thumb"
                          animate={{ x: enabled ? 20 : 2 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        />
                      </button>
                    </div>
                    <p className="ap-type-desc">{meta.desc}</p>
                    {cfg && (
                      <div className="ap-type-meta">
                        {cfg.last_run_at && (
                          <span className="ap-type-last-run">
                            Last: {new Date(cfg.last_run_at).toLocaleString()}
                          </span>
                        )}
                        {cfg.next_run_at && enabled && (
                          <span className="ap-type-next-run">
                            Next: {new Date(cfg.next_run_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {deptRuns.length > 0 && (
              <div className="ap-runs-log">
                <span className="ap-runs-label">Recent runs:</span>
                {deptRuns.map(run => (
                  <span key={run.id} className={`ap-run-item ap-run-${run.status}`}>
                    {TYPE_META[run.automation_type]?.icon} {run.automation_type.replace('_',' ')}
                    {' '}· {run.status}
                    {run.detail?.items ? ` · ${run.detail.items} items` : ''}
                    {' '}· {new Date(run.started_at).toLocaleTimeString()}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AutomationPanel = () => {
  const { departments: dbDepts } = useDepartments();
  const [status, setStatus] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalPaused, setGlobalPaused] = useState(false);

  // Map v_california_departments rows to { code (acronym), bu (BU code), name }
  const departments = dbDepts.map(d => ({
    code: d.acronym,
    bu: d.code,
    name: d.name,
  }));

  const fetchAll = useCallback(async () => {
    try {
      const [st, cfgs, rs] = await Promise.all([
        apiClient.get('/api/automation/status'),
        apiClient.get('/api/automation/configs'),
        apiClient.get('/api/automation/runs?limit=50'),
      ]);
      setStatus(st);
      setConfigs(Array.isArray(cfgs) ? cfgs : []);
      setRuns(Array.isArray(rs) ? rs : []);
      setGlobalPaused(!st?.running);
    } catch(err) {
      console.error('AutomationPanel fetch error:', err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async (deptCode, deptName, type, enable, existingId) => {
    try {
      if (existingId) {
        await apiClient.put('/api/automation/configs/' + existingId, { enabled: enable });
      } else {
        await apiClient.post('/api/automation/configs', {
          dept_code: deptCode,
          dept_name: deptName,
          automation_type: type,
          enabled: enable,
          priority: 10,
        });
      }
      await fetchAll();
    } catch (err) {
      // Silent console-only failure left the toggle looking saved until the
      // next refetch snapped it back (slice 4).
      console.error('Toggle failed:', err);
      toast(`Couldn’t update ${deptCode} automation — ${err.message}`, 'error');
    }
  };

  const handleGlobalPause = async () => {
    if (!globalPaused) {
      const ok = await confirmDialog({
        title: 'Pause all automation',
        message: 'Stops autonomous scraping and classification for every department until resumed. Pause?',
        confirmLabel: 'Pause all',
      });
      if (!ok) return;
    }
    try {
      if (globalPaused) await apiClient.post('/api/automation/resume', {});
      else await apiClient.post('/api/automation/pause', {});
      setGlobalPaused(p => !p);
      toast(globalPaused ? 'Automation resumed' : 'Automation paused');
      await fetchAll();
    } catch (err) {
      console.error('Global pause failed:', err);
      toast(`Couldn’t ${globalPaused ? 'resume' : 'pause'} automation — ${err.message}`, 'error');
    }
  };

  if (loading) return (
    <div className="ap-loading">
      <RefreshCw size={20} className="ap-spin" />
      <span>Loading automation status...</span>
    </div>
  );

  const enabledCount = configs.filter(c => c.enabled).length;

  return (
    <div className="ap-container">
      {/* Global header */}
      <div className="ap-global-header">
        <div className="ap-global-status">
          <StatusDot running={!globalPaused} />
          <span className="ap-global-label">
            Automation Engine {globalPaused ? 'paused' : 'running'}
          </span>
          <span className="ap-global-count">{enabledCount} active configs</span>
        </div>
        <div className="ap-global-actions">
          <button className="ap-btn ap-btn-refresh" onClick={fetchAll} title="Refresh">
            <RefreshCw size={14} />
          </button>
          <button
            className={`ap-btn ${globalPaused ? 'ap-btn-resume' : 'ap-btn-pause'}`}
            onClick={handleGlobalPause}
          >
            {globalPaused ? <><Play size={14} /> Resume All</> : <><Pause size={14} /> Pause All</>}
          </button>
        </div>
      </div>

      {/* Manual-first notice */}
      <div className="ap-notice">
        <Activity size={13} />
        Manual jobs always take priority — automation pauses automatically when you queue anything.
      </div>

      {/* Department list */}
      <div className="ap-dept-list">
        {departments.map(dept => (
          <DeptRow
            key={dept.code}
            dept={dept}
            configs={configs}
            runs={runs}
            onToggle={handleToggle}
          />
        ))}
      </div>
    </div>
  );
};

export default AutomationPanel;
